/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import GenericCard from "@ui-components/Card/GenericCard";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { ReactComponent as SearchIcon } from "@shared/assets/Icons/IconsSearch.svg";
import React, { useMemo, useState } from "react";
import RuleNotificationRow from "./RuleNotificationRow";
import GenericModal from "@ui-components/Modal/GenericModal";
import DefaultRecipientsManager from "../DefaultRecipientsManager";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import {
  AlertTemplate,
  CustomizeAlertRequest,
  Notification,
} from "../NotificationTypes";
import {
  createAlertParameters,
  enableTemplate,
  updateAlertParameters,
  updateSubscriptionEnabled,
} from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";

import { useAppSelector } from "@redux/hooks";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { ServiceItem, useCustomers } from "../hooks/useCustomers";
import { useSubscriptions } from "../hooks/useSubscriptons";
import { getTemplateLabel } from "../Helpers/formatters";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";

interface CustomerPort {
  name: string;
  ports: { portName: string }[];
}
interface CustomerConnection {
  name: string;
  connection: { connectionName: string }[];
}
interface RulesSectionProps {
  title: string;
  type: "port" | "internet" | "cloud-connectivity";
  services: ServiceItem[];
  customers: CustomerPort[] | CustomerConnection[]; //HERE
  isReadOnly?: boolean;
  initialExpanded?: boolean;
  notificationStates: ServiceNotificationStatus[];
  templates: AlertTemplate[];
  tenantType?: string; //HERE
  sendTenantId?: boolean;
  subscriberType:  "user" | "tenant";
  subscriberId: string;
  showManageRecipients?: boolean;
}
export interface CustomAlertDetails {
  id: string;
  name: string;
  description: string;
  severity: string;
  interval: string;
  conditions: {
    operator: string;
    threshold: number;
  };
}
export interface ServiceNotificationStatus {
  id: string;
  serviceId: string;
  notificationId: string;
  subscriberId: string;
  subscriptionId: string;
  subscriberName: string;
  enabled: boolean;
  recipients: string[];
  subscription?: {
    id: string;
    subscriberType: string;
    subscriberId: string;
    serviceType: string;
    serviceId: string;
    enabled: boolean;
    subscriberName: string;
    interval?: string;
    conditions?: { operator: string; value: number }[];
  };
  customAlert: CustomAlertDetails;
}

export function RulesSection({
  title,
  customers,
  type,
  isReadOnly = false,
  initialExpanded = false,
  templates,
  subscriberType, // "user" | "tenant"
  subscriberId,
  sendTenantId,
  showManageRecipients = false,
}: RulesSectionProps) {
  const api = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const tenantType = useAppSelector((s) => s.userProfile.TenantType);
  // const subscriberType: "user" | "tenant" =
  //   tenantType === "CUSTOMER" ? "user" : "tenant";

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  );
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null
  );

  //This is used to render for the customers only the service in the filter component
  const isCustomerView = tenantType === "CUSTOMER";
  const [isFetching, setIsFetching] = useState(false);
  const overlaySpinner = useProcessingOverlay(isFetching);

  //Load the customerOptions on mount from usage
  const {
    items: usageItems,
    options: customerOptions,
    loading: loadingOptions,
  } = useCustomers(tenantId, isCustomerView, type);
  

  //Fetch subscriptions once the user picks one customer and service
  const {
    subs: serviceNotifications,
    loadingSet: loadingSubs,
    reload: reloadSubscriptions,
  } = useSubscriptions(
    type,
    selectedCustomerId,
    selectedServiceId,
    templates,
    subscriberType,
    subscriberId
  );

  const onCustomerSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    const found = usageItems.find((i) => String(i.id) === serviceId);
    setSelectedCustomerId(found?.customer?.id.toString() ?? null);
  };

  // TEMP: Set default recipients statically for now
  const defaultRecipients = useMemo<string[]>(() => {
    if (customers.length === 0) return [];
    return [
      "All network admins",
      "external@gmail.com",
      "contractor@freelance.com",
    ];
  }, [customers.length]);

  const [recipientsModalNotification, setRecipientsModalNotification] =
    useState<Notification | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);

  //-----------------------------------------
  // Enable template
  const handleEnable = async (
    alertTemplateId: string,
    serviceType: string,
    backendServiceId: string
  ) => {
    setIsFetching(true);
    try {
      const payload = {
        alertTemplateId,
        serviceType,
        serviceId: backendServiceId,
      };
      const resp = await enableTemplate(
        api,
        payload,
        sendTenantId ? tenantId : undefined
      );

      const newSub = resp.data.data.subscription;

      await reloadSubscriptions();
    } catch (err) {
      console.error(`Failed to enable ${alertTemplateId}`, err);
    } finally {
      setIsFetching(false);
    }
  };

  // Disable subscription
  const handleDisableSubscription = async (subscriptionId: string) => {
    setIsFetching(true);
    try {
      await updateSubscriptionEnabled(api, subscriptionId, false);
      await reloadSubscriptions();
    } catch (err) {
      console.error(`Failed to disable subscription ${subscriptionId}`, err);
    } finally {
      setIsFetching(false);
    }
  };
  //Enable again the subscription (we set update because it already exists, we just disabled it before)
  const handleEnableSubscription = async (subscriptionId: string) => {
    setIsFetching(true);
    try {
      await updateSubscriptionEnabled(api, subscriptionId, true);
      await reloadSubscriptions();
    } catch (err) {
      console.error(`Failed to enable subscription ${subscriptionId}`, err);
    } finally {
      setIsFetching(false);
    }
  };
  //-----------------------------------------
  const handleManageRecipients = (notif: Notification) => {
    setRecipientsModalNotification(notif);
    setShowRecipientsModal(true);
  };

  //Save params of the updated alert
  const handleSaveParams = async (
    alertInstanceId: string,
    threshold: string,
    interval: string,
    operator: string
  ) => {
    //Bail early if we haven’t got the basics
    if (!selectedCustomerId) {
      console.warn("No subscriberId to customize alert for!");
      return;
    }
    const notif = serviceNotifications.find(
      (n) => n.subscription.alertId === alertInstanceId
    );
    if (!notif) {
      console.error(
        `No matching subscription for alertInstanceId ${alertInstanceId}`
      );
      return;
    }
    const tpl = templates.find((t) => t.id === notif.templateId)!;
    const meta = tpl.thresholdMetadata;
    //Create an object typed as customParameters
    const customParameters: CustomizeAlertRequest["customParameters"] = {
      //conditions: [], // start empty; we’ll push into it if the user changed threshold
      interval: "", // start as empty string; overwrite below if the user changed interval
      //severity
      //tags: [],
    };

    //Populate it only if the user actually edited those fields
    if (
      meta?.customizable &&
      threshold.trim() !== "" &&
      !isNaN(Number(threshold))
    ) {
      customParameters.conditions = [
        {
          type: "threshold",
          operator,
          value: Number(threshold),
        },
      ];
    }

    //Overwrite the default interval if they set one
    if (interval) {
      customParameters.interval = interval;
    }

    //Now you can safely build your full request
    const payload: CustomizeAlertRequest = {
      subscriberType,
      subscriberId,
      customParameters,
    };

    try {
      if (notif.isCustomized) {
        await updateAlertParameters(api, notif.customAlert.id, payload);
      } else {
        await createAlertParameters(api, alertInstanceId, payload);
      }
      await reloadSubscriptions();
    } catch (err) {
      console.error(`Failed to save parameters for ${alertInstanceId}`, err);
    }
  };

    const tableSkeleton = (
      <SkeletonTableRows rows={3} cols={1} rowHeight={16} />
    );

  return (
    <div className="my-2">
      {overlaySpinner}
      <GenericCard
        headerContent={title}
        isCollapsible
        initialExpanded={initialExpanded}
        rightContent={
          isReadOnly && (
            <GenericTag variant="neutral" size="small" label="Read Only" />
          )
        }
        bodyContent={
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="relative">
                <SearchSelect
                  placeholder={
                    isCustomerView
                      ? "Select a service…"
                      : "Select a customer or service…"
                  }
                  options={customerOptions}
                  defaultValue={selectedServiceId ?? undefined}
                  leftIcon={
                    <SearchIcon className="w-5 h-5 text-semantic-text-secondary" />
                  }
                  onSelect={onCustomerSelect}
                  onSearch={(q) => setSearchTerm(q ?? "")}
                  className="flex-1"
                  widthClass="w-84"
                  loading={loadingOptions}
                />
              </div>
            </div>

            {loadingSubs.has(selectedServiceId!) ? (
              // skeleton for “we’re loading subscriptions”
              <div className="space-y-3">
                <LoadingWrapper
                  isLoading={isFetching}
                  skeleton={tableSkeleton}
                ></LoadingWrapper>
              </div>
            ) : templates.length === 0 ? (
              <p className="text-center text-sm text-semantic-text-secondary border rounded-md border-gray-300 py-5">
                No templates available
              </p>
            ) : (
              templates.map((tpl) => {
                const notif = serviceNotifications.find(
                  (n) => n.templateId === tpl.id
                );
                const subscription = notif?.subscription;
                const rowLoading = subscription
                  ? loadingSubs.has(subscription.id)
                  : false;
                return (
                  <RuleNotificationRow
                    key={tpl.id}
                    notification={{
                      template: tpl,
                      subscription,
                    }}
                    enabled={subscription?.enabled ?? false}
                    isLoading={rowLoading}
                    isReadOnly={isReadOnly}
                    defaultRecipients={notif?.recipients ?? defaultRecipients}
                    onEnable={() =>
                      subscription
                        ? handleEnableSubscription(subscription.id)
                        : handleEnable(tpl.id, type, selectedServiceId!)
                    }
                    onDisable={() =>
                      subscription && handleDisableSubscription(subscription.id)
                    }
                    onManageRecipients={() =>
                      subscription && handleManageRecipients(notif)
                    }
                    onSaveParams={handleSaveParams}
                    requireSelection={!selectedServiceId}
                    showManageRecipients={showManageRecipients}
                  />
                );
              })
            )}
          </div>
        }
      />

      {showRecipientsModal && recipientsModalNotification && (
        <GenericModal
          isOpen
          onClose={() => setShowRecipientsModal(false)}
          title={(() => {
            const tpl = templates.find(
              (t) => t.id === recipientsModalNotification.templateId
            );
            return tpl
              ? getTemplateLabel(tpl.id, tpl.name)
              : recipientsModalNotification.templateId;
          })()}
      
        >
          <DefaultRecipientsManager
            subscriptionId={recipientsModalNotification.subscriptionId}
            notificationChannels={
              recipientsModalNotification.notificationChannels
            }
            initialRecipients={serviceNotifications
              .filter(
                (p) =>
                  p.notificationId ===
                    recipientsModalNotification?.notificationId &&
                  p.serviceId === recipientsModalNotification?.serviceId
              )
              .flatMap((p) => p.recipients)}
            onSaveSuccess={async () => {
              await reloadSubscriptions();
              setShowRecipientsModal(false);
            }}
          />
        </GenericModal>
      )}
    </div>
  );
}

export default RulesSection;
